// metronic_8's donut chart: draws the ring with ApexCharts (window.ApexCharts, from the theme's
// plugins bundle, the library Metronic's own charts use) in place of the base tier's SVG, and keeps
// the base selection behaviour: clicking a legend line or a slice shows "value / total" in the centre,
// clicking anywhere else shows the total again. ApexCharts shows its own selected slice; highlight()
// keeps it in step with the legend.
const donutChart = require('../../../../../core/themes/base/components/donutChart/donutChart');

// ApexCharts adds its stylesheet as a <style id="apexcharts-css"> unless an element with that id
// already exists. The CSP blocks that tag, and the same CSS already ships in the plugins bundle, so an
// empty element with that id tells ApexCharts its stylesheet is there. (The <style> it adds for the
// legend of every chart, even a hidden legend, cannot be avoided: the CSP blocks it, to no effect.)
function markApexStylesheetPresent() {
    if (document.getElementById('apexcharts-css') === null) {
        let marker = document.createElement('meta');

        marker.id = 'apexcharts-css';
        document.head.appendChild(marker);
    }
}

// A computed colour ("rgb(r, g, b)") as hex, the form ApexCharts shades reliably
function rgbToHex(color) {
    let channels = color.match(/\d+/g);

    if (channels === null || channels.length < 3) {
        return color;
    }

    return '#' + channels.slice(0, 3).map((channel) => parseInt(channel, 10).toString(16).padStart(2, '0')).join('');
}

const metronicDonutChart = Object.assign({}, donutChart, {
    subelements: Object.assign({}, donutChart.subelements, {
        plot: null,
    }),
    apexChart: null,            // the ApexCharts instance; null when nothing is drawn (no ApexCharts, or nothing to count)
    apexSelectedIndex: null,    // the slice ApexCharts shows selected
    syncingApex: false,         // true while this code toggles a slice, so the event ApexCharts fires back is ignored

    init: function () {
        donutChart.init.call(this);

        this.renderChart();
    },

    renderChart: function () {
        // the template draws a plain empty ring, and no plot, when there is nothing to count
        if (!this.hasSubelement('plot') || typeof window.ApexCharts === 'undefined') {
            return;
        }

        markApexStylesheetPresent();

        // one slice per legend line, in the same order, so a slice's index is its element's index. A slice of 0
        // takes no room
        let legendItems = this.element.querySelectorAll('button[data-zit-chart-index]');
        let series = [];
        let labels = [];
        let colors = [];

        legendItems.forEach((legendItem) => {
            let marker = legendItem.querySelector('.zit-donut-marker');

            series.push(Number(legendItem.dataset.zitChartValue));
            labels.push(legendItem.dataset.zitChartLabel);
            colors.push(marker !== null ? rgbToHex(getComputedStyle(marker).color) : '#a1a5b7');
        });

        // the gap between two slices is drawn in the card's own colour
        let surfaceColor = rgbToHex(getComputedStyle(this.element).backgroundColor);

        this.apexChart = new window.ApexCharts(this.subelements.plot, {
            series: series,
            labels: labels,
            colors: colors,
            chart: {
                type: 'donut',
                height: '100%',
                fontFamily: 'inherit',
                sparkline: {
                    enabled: true
                },
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        this.onSliceSelection(config);
                    }
                }
            },
            legend: {
                show: false
            },
            dataLabels: {
                enabled: false
            },
            tooltip: {
                fillSeriesColor: false
            },
            stroke: {
                width: 2,
                colors: [surfaceColor]
            },
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    donut: {
                        size: '72%',
                        labels: {
                            show: false
                        }
                    }
                }
            },
            states: {
                hover: {
                    filter: {
                        type: 'none'
                    }
                },
                active: {
                    allowMultipleDataPointsSelection: false,
                    filter: {
                        type: 'darken',
                        value: 0.25
                    }
                }
            }
        });

        this.apexChart.render();
    },

    // A slice was clicked: ApexCharts has already toggled it, so only the centre and the legend follow
    onSliceSelection: function (config) {
        if (this.syncingApex) {
            return;
        }

        let index = config.dataPointIndex;
        let selectedSlices = Array.isArray(config.selectedDataPoints) && Array.isArray(config.selectedDataPoints[0]) ? config.selectedDataPoints[0] : [];
        let isSelected = selectedSlices.indexOf(index) !== -1;

        this.apexSelectedIndex = isSelected ? index : null;

        if (isSelected) {
            this.select(index);
        } else {
            this.deselect();
        }
    },

    // The legend lines as the base marks them, and the slice ApexCharts shows selected
    highlight: function (index) {
        donutChart.highlight.call(this, index);

        this.syncApexSelection(index);
    },

    syncApexSelection: function (index) {
        // an element of 0 has no slice drawn, so no slice shows it selected
        let sliceIndex = index !== null && Number(this.elementValue(index)) > 0 ? index : null;

        if (this.apexChart === null || sliceIndex === this.apexSelectedIndex) {
            return;
        }

        this.syncingApex = true;

        // a slice is selected or unselected by toggling it
        if (this.apexSelectedIndex !== null) {
            this.apexChart.toggleDataPointSelection(this.apexSelectedIndex);
        }

        if (sliceIndex !== null) {
            this.apexChart.toggleDataPointSelection(sliceIndex);
        }

        this.syncingApex = false;
        this.apexSelectedIndex = sliceIndex;
    },

    // A click on a slice selects too
    isSelectionClick: function (target) {
        if (donutChart.isSelectionClick.call(this, target)) {
            return true;
        }

        return target instanceof Element && this.element.contains(target) && target.closest('.apexcharts-pie-area') !== null;
    },
});

module.exports = metronicDonutChart;
